// ---------------------------------------------------------------------------
// PWA Caisse Tablette — useProducts hook
// Loads products from the API on mount, falls back to IndexedDB cache
// if the network request fails (offline mode).
// ---------------------------------------------------------------------------

import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/services/api-client';
import { cacheProducts, getCachedProducts } from '@/services/product-cache';
import type { Product } from '@/lib/types';

// ---------------------------------------------------------------------------
// Return type
// ---------------------------------------------------------------------------

interface UseProductsReturn {
  /** The list of processed products (prices in euros). */
  products: Product[];
  /** Whether the initial load is in progress. */
  loading: boolean;
  /** Manually refresh products from the API. */
  refresh: () => Promise<void>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Fetches products from the backend on mount. If the API call fails
 * (offline), falls back to the IndexedDB product cache.
 *
 * Prices are automatically converted from centimes to euros via
 * `processProducts` inside `apiClient.getProducts()`.
 */
export function useProducts(): UseProductsReturn {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  const loadProducts = useCallback(async () => {
    setLoading(true);

    try {
      // Try API first
      const response = await apiClient.getProducts();
      const items = response.products ?? [];
      setProducts(items);

      // Cache for offline fallback
      if (items.length > 0) {
        await cacheProducts(items);
      }
    } catch (error) {
      console.warn(
        '[useProducts] API failed, falling back to cache:',
        (error as Error).message,
      );

      // Fallback to IndexedDB cache
      try {
        const cached = await getCachedProducts();
        setProducts(cached);
      } catch (cacheError) {
        console.error(
          '[useProducts] Cache fallback also failed:',
          (cacheError as Error).message,
        );
        setProducts([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  return { products, loading, refresh: loadProducts };
}
